import { useMemo } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { resolveKeptRef } from '@/components/keep/kept-ref';
import { MailboxEnvelopeRow } from '@/components/mailbox/MailboxEnvelopeRow';
import { useMailboxImportedRefs } from '@/hooks/useMemberExternalAccountQueries';
import type { MailboxEnvelopeRowData } from '@/utils/mailbox-envelope-row';
import { MailboxKeepActions } from './MailboxKeepActions';

type MailboxEnvelopeListItem = Extract<ConversationItem, { kind: 'mailbox_envelope_list' }>;
type MailboxEnvelopeListRow = MailboxEnvelopeListItem['items'][number];

export interface ProvenanceMailboxEnvelopeListCardProps {
  item: MailboxEnvelopeListItem;
}

function toRowData(row: MailboxEnvelopeListRow): MailboxEnvelopeRowData {
  return {
    providerMessageId: row.providerMessageId,
    subject: row.subject,
    fromName: row.fromName,
    fromEmail: row.fromEmail,
    receivedAt: row.receivedAt,
    snippet: row.snippet,
    hasAttachments: row.hasAttachments,
    attachmentCount: typeof row.attachmentCount === 'number' ? row.attachmentCount : row.attachmentNames.length,
    folders: row.folders,
    webLink: row.webLink,
  };
}

export function ProvenanceMailboxEnvelopeListCard({ item }: ProvenanceMailboxEnvelopeListCardProps) {
  const ids = useMemo(() => item.items.map((row) => row.providerMessageId), [item.items]);
  const refsQuery = useMailboxImportedRefs(item.accountId, ids);
  const providerLabel = item.productLabel;
  const count = item.items.length;
  const noun = count === 1 ? 'message' : 'messages';
  const title = item.listRole === 'SEARCH' ? `${count} ${noun} in ${item.accountEmail}` : `Conversation · ${count} ${noun}`;
  const suffix = item.hasMore ? (item.listRole === 'SEARCH' ? ' · more available' : ' · truncated') : null;

  const keptByRow = item.items.map((row) =>
    resolveKeptRef(refsQuery.data, row.providerMessageId, row.importedContentId, row.importedCode)
  );
  const anyUnkept = keptByRow.some((kept) => kept === null);

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 shrink-0" />
        <p className="min-w-0 truncate font-medium">
          {title}
          {suffix ? <span className="font-normal text-xs text-muted-foreground">{suffix}</span> : null}
        </p>
        <Badge variant="outline" className="ml-auto shrink-0">
          {providerLabel}
        </Badge>
      </div>
      <ul className="mt-2 divide-y divide-border">
        {item.items.map((row, index) => (
          <MailboxEnvelopeRow
            key={row.providerMessageId}
            row={toRowData(row)}
            accountId={item.accountId}
            providerLabel={providerLabel}
            kept={keptByRow[index]}
          />
        ))}
      </ul>
      {item.listRole === 'THREAD' && anyUnkept ? (
        <div className="mt-2 border-t border-border pt-2">
          <MailboxKeepActions scope="conversation" accountId={item.accountId} providerMessageIds={ids} kept={null} />
        </div>
      ) : null}
    </div>
  );
}
